import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import Sidebar from "../components/Sidebar";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { 
  CreditCard, 
  Calendar, 
  Upload,
  CheckCircle2,
  XCircle,
  Clock,
  Receipt,
  MapPin,
  AlertTriangle,
  Plus,
  Eye,
  FileText,
  Download
} from "lucide-react";

export default function TenantPaymentPortal() {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [upcomingPayment, setUpcomingPayment] = useState(null);
  const [payments, setPayments] = useState([]);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [viewingPayment, setViewingPayment] = useState(null);
  
  const [formData, setFormData] = useState({
    amount: "",
    payment_date: "",
    reference_number: "",
    notes: ""
  });
  const [proofFile, setProofFile] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [upcomingRes, paymentsRes] = await Promise.all([
        axios.get(`${API}/payments/my/upcoming`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/payments/my`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setUpcomingPayment(upcomingRes.data);
      setPayments(paymentsRes.data);
    } catch (error) {
      toast.error("Failed to load payment data");
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = ["image/jpeg", "image/png", "image/webp", "application/pdf"];
      if (!allowedTypes.includes(file.type)) {
        toast.error("Please upload an image (JPEG, PNG, WebP) or PDF file");
        return;
      }
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        toast.error("File size must be less than 10MB");
        return;
      }
      setProofFile(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.amount || !formData.payment_date || !proofFile) {
      toast.error("Please fill in all required fields and upload payment proof");
      return;
    }

    setSubmitting(true);
    try {
      const submitData = new FormData();
      submitData.append("amount", formData.amount);
      submitData.append("payment_date", formData.payment_date);
      submitData.append("reference_number", formData.reference_number || "");
      submitData.append("notes", formData.notes || "");
      submitData.append("payment_proof", proofFile);

      await axios.post(`${API}/payments`, submitData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });

      toast.success("Payment submitted! Awaiting admin confirmation.");
      setShowPaymentForm(false);
      setFormData({ amount: "", payment_date: "", reference_number: "", notes: "" });
      setProofFile(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit payment");
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return new Date(dateString).toLocaleDateString('en-NG', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return "-";
    return `₦${amount}`;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "confirmed":
        return (
          <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            Confirmed
          </Badge>
        );
      case "rejected":
        return (
          <Badge className="bg-red-100 text-red-700 hover:bg-red-100">
            <XCircle className="w-3 h-3 mr-1" />
            Rejected
          </Badge>
        );
      default:
        return (
          <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">
            <Clock className="w-3 h-3 mr-1" />
            Pending
          </Badge>
        );
    }
  };

  const getDaysUntilDue = () => {
    if (!upcomingPayment?.next_payment_date) return null;
    try {
      const dueDate = new Date(upcomingPayment.next_payment_date);
      const today = new Date();
      const diffTime = dueDate - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    } catch {
      return null;
    }
  };

  const daysUntilDue = getDaysUntilDue();

  return (
    <div className="flex min-h-screen bg-background" data-testid="tenant-payment-portal">
      <Sidebar />
      
      <main className="flex-1 ml-0 md:ml-64 p-4 pt-20 md:pt-6 md:p-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-primary flex items-center gap-3">
                <CreditCard className="w-8 h-8" />
                Payment Portal
              </h1>
              <p className="text-text-muted mt-1">View and submit your rent payments</p>
            </div>
            <Button
              onClick={() => setShowPaymentForm(true)}
              className="bg-primary hover:bg-primary-800"
              data-testid="mark-as-paid-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Mark Payment as Paid
            </Button>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-pulse text-primary">Loading...</div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Upcoming Payment Card */}
              {upcomingPayment ? (
                <Card className={`border-2 ${daysUntilDue !== null && daysUntilDue <= 7 ? 'border-amber-300 bg-amber-50/50' : 'border-stone-200'}`}>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-primary" />
                      Upcoming Payment
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="text-center p-4 bg-white rounded-xl border border-stone-200">
                        <p className="text-sm text-text-muted mb-1">Next Due Date</p>
                        <p className="text-2xl font-bold text-primary">
                          {formatDate(upcomingPayment.next_payment_date)}
                        </p>
                        {daysUntilDue !== null && (
                          <p className={`text-sm mt-1 ${daysUntilDue <= 3 ? 'text-red-600 font-medium' : daysUntilDue <= 7 ? 'text-amber-600' : 'text-green-600'}`}>
                            {daysUntilDue <= 0 ? 'Overdue!' : `${daysUntilDue} day(s) remaining`}
                          </p>
                        )}
                      </div>
                      <div className="text-center p-4 bg-white rounded-xl border border-stone-200">
                        <p className="text-sm text-text-muted mb-1">Expected Amount</p>
                        <p className="text-2xl font-bold text-green-600">
                          {formatCurrency(upcomingPayment.last_payment_amount)}
                        </p>
                        <p className="text-sm text-text-muted mt-1">
                          {upcomingPayment.payment_frequency}
                        </p>
                      </div>
                      <div className="text-center p-4 bg-white rounded-xl border border-stone-200">
                        <p className="text-sm text-text-muted mb-1">Location</p>
                        <p className="text-lg font-medium text-primary flex items-center justify-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {upcomingPayment.location}
                        </p>
                        <p className="text-sm text-text-muted mt-1">
                          Apartment #{upcomingPayment.apartment_number}
                        </p>
                      </div>
                    </div>

                    {daysUntilDue !== null && daysUntilDue <= 7 && (
                      <div className="mt-4 p-3 bg-amber-100 rounded-lg flex items-start gap-2">
                        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-amber-800">Payment Due Soon</p>
                          <p className="text-xs text-amber-700">
                            Please make your payment and submit proof to avoid late fees.
                          </p>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <Card className="border-stone-200">
                  <CardContent className="py-8 text-center">
                    <Receipt className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                    <p className="text-text-muted">No tenant information found</p>
                    <p className="text-sm text-stone-400 mt-1">
                      Please complete your tenant information first
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Payment History */}
              <Card className="border-stone-200">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Receipt className="w-5 h-5" />
                    Payment History ({payments.length})
                  </CardTitle>
                  <CardDescription>
                    Your submitted payments and their confirmation status
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {payments.length === 0 ? (
                    <div className="text-center py-8">
                      <FileText className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                      <p className="text-text-muted">No payment records yet</p>
                      <p className="text-sm text-stone-400 mt-1">
                        Submit your first payment using the button above
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {payments.map((payment) => (
                        <div 
                          key={payment.id}
                          className="flex items-center justify-between p-4 bg-stone-50 rounded-xl hover:bg-stone-100 transition-colors"
                          data-testid={`payment-${payment.id}`}
                        >
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                              <CreditCard className="w-5 h-5 text-primary" />
                            </div>
                            <div>
                              <p className="font-medium text-primary">
                                {formatCurrency(payment.amount)}
                              </p>
                              <p className="text-sm text-text-muted">
                                Paid: {formatDate(payment.payment_date)} • Submitted: {formatDate(payment.submitted_at)}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {getStatusBadge(payment.status)}
                            {payment.status === "confirmed" && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/payments/${payment.id}/receipt`, '_blank')}
                                className="text-green-600 border-green-200 hover:bg-green-50"
                                data-testid={`download-receipt-${payment.id}`}
                              >
                                <Download className="w-4 h-4 mr-1" />
                                Receipt
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setViewingPayment(payment)}
                              data-testid={`view-payment-${payment.id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </main>

      {/* Submit Payment Dialog */}
      <Dialog open={showPaymentForm} onOpenChange={setShowPaymentForm}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Submit Payment</DialogTitle>
            <DialogDescription>
              Fill in your payment details and upload proof of payment
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="amount">Amount Paid (₦) *</Label>
                <Input
                  id="amount"
                  data-testid="payment-amount-input"
                  placeholder="e.g., 150,000"
                  value={formData.amount}
                  onChange={(e) => setFormData({...formData, amount: e.target.value})}
                  className="h-11"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="payment_date">Payment Date *</Label>
                <Input
                  id="payment_date"
                  data-testid="payment-date-input"
                  type="date"
                  value={formData.payment_date}
                  onChange={(e) => setFormData({...formData, payment_date: e.target.value})}
                  className="h-11"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="reference_number">Reference/Receipt Number</Label>
              <Input
                id="reference_number"
                data-testid="payment-reference-input"
                placeholder="e.g., TRX123456789"
                value={formData.reference_number}
                onChange={(e) => setFormData({...formData, reference_number: e.target.value})}
                className="h-11"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Additional Notes</Label>
              <Textarea
                id="notes"
                data-testid="payment-notes-input"
                placeholder="Any additional information..."
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label>Payment Proof *</Label>
              <div className="border-2 border-dashed border-stone-300 rounded-xl p-4 text-center hover:border-primary transition-colors">
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,application/pdf"
                  onChange={handleFileChange}
                  className="hidden"
                  id="proof-upload"
                  data-testid="payment-proof-input"
                />
                <label htmlFor="proof-upload" className="cursor-pointer">
                  <Upload className="w-8 h-8 text-stone-400 mx-auto mb-2" />
                  {proofFile ? (
                    <p className="text-sm text-primary font-medium">{proofFile.name}</p>
                  ) : (
                    <>
                      <p className="text-sm text-text-muted">Click to upload payment proof</p>
                      <p className="text-xs text-stone-400 mt-1">JPEG, PNG, WebP or PDF (max 10MB)</p>
                    </>
                  )}
                </label>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowPaymentForm(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-primary hover:bg-primary-800"
                disabled={submitting}
                data-testid="submit-payment-btn"
              >
                {submitting ? "Submitting..." : "Submit Payment"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Payment Details Dialog */}
      <Dialog open={!!viewingPayment} onOpenChange={() => setViewingPayment(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Payment Details</DialogTitle>
            <DialogDescription>
              Complete information about this payment
            </DialogDescription>
          </DialogHeader>
          
          {viewingPayment && (
            <div className="space-y-4 py-4">
              <div className="flex justify-between items-center">
                <span className="text-text-muted">Status</span>
                {getStatusBadge(viewingPayment.status)}
              </div>

              {viewingPayment.status === "rejected" && viewingPayment.rejection_reason && (
                <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                  <p className="text-sm font-medium text-red-800">Rejection Reason:</p>
                  <p className="text-sm text-red-700">{viewingPayment.rejection_reason}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-text-muted">Amount</span>
                  <p className="font-medium text-green-600">{formatCurrency(viewingPayment.amount)}</p>
                </div>
                <div>
                  <span className="text-text-muted">Payment Date</span>
                  <p className="font-medium">{formatDate(viewingPayment.payment_date)}</p>
                </div>
                <div>
                  <span className="text-text-muted">Reference Number</span>
                  <p className="font-medium">{viewingPayment.reference_number || "-"}</p>
                </div>
                <div>
                  <span className="text-text-muted">Submitted</span>
                  <p className="font-medium">{formatDate(viewingPayment.submitted_at)}</p>
                </div>
              </div>

              {viewingPayment.notes && (
                <div>
                  <span className="text-sm text-text-muted">Notes</span>
                  <p className="text-sm mt-1 p-2 bg-stone-50 rounded">{viewingPayment.notes}</p>
                </div>
              )}

              <div>
                <span className="text-sm text-text-muted">Payment Proof</span>
                <div className="mt-2">
                  {viewingPayment.proof_url && (
                    viewingPayment.proof_url.endsWith('.pdf') ? (
                      <a 
                        href={`${process.env.REACT_APP_BACKEND_URL}${viewingPayment.proof_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-primary hover:underline"
                      >
                        <FileText className="w-4 h-4" />
                        View PDF
                      </a>
                    ) : (
                      <img 
                        src={`${process.env.REACT_APP_BACKEND_URL}${viewingPayment.proof_url}`}
                        alt="Payment Proof"
                        className="max-w-full h-auto rounded-lg border border-stone-200"
                      />
                    )
                  )}
                </div>
              </div>

              {viewingPayment.confirmed_at && (
                <div className="text-xs text-text-muted pt-2 border-t">
                  {viewingPayment.status === "confirmed" ? "Confirmed" : "Processed"} on {formatDate(viewingPayment.confirmed_at)}
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4">
                {viewingPayment.status === "confirmed" && (
                  <Button
                    variant="outline"
                    onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/payments/${viewingPayment.id}/receipt`, '_blank')}
                    className="text-green-600 border-green-200 hover:bg-green-50"
                    data-testid="download-receipt-dialog-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download Receipt
                  </Button>
                )}
                <Button variant="outline" onClick={() => setViewingPayment(null)}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
